import React, { useMemo } from "react";

import { Box, Button, Typography } from "@mui/material";

import {
  UnavailableButton,
  VehicleImageCarousel,
  VehicleDescriptionAndFeatures,
  VehicleUnavailable,
} from "../VehicleCard/components";
import { VehiclePricingText } from "../VehicleCard/components/VehiclePricingSelect/components";
import { ArrowRightIcon, PassengerIcon } from "design-system/icons";
import { grayLight, white, black, grayDark } from "design-system/colors";
import MissingImage from "design-system/images/MissingImage.png";
import { useScreenSize } from "globals/hooks";

type VehicleCategoryCardProps = {
  category: {
    id: string;
    name: string;
    capacity: number;
    description?: string;
    operator?: {
      id: string;
      name: string;
    };
    photos?: Array<{
      url: string;
      index: number;
    }>;
    features?: Array<{
      id: string;
      name: string;
      category: string;
    }>;
    baseRateAutomation?: {
      total: number;
    };
    hasTripConflict?: boolean | null;
    hasUnavailabilityBlock?: boolean | null;
  };
  onCategorySelect: () => void;
};

function VehicleCategoryCard(props: VehicleCategoryCardProps) {
  const { category, onCategorySelect } = props;

  // hooks
  const { isMobileView } = useScreenSize();

  // Format photos for the carousel
  const formattedPhotos = useMemo(() => {
    return (
      category.photos?.map((photo, index) => ({
        id: `${category.id}-photo-${index}`,
        url: photo.url || MissingImage,
        photoIndex: photo.index || index,
      })) || [
        {
          id: `${category.id}-default`,
          url: MissingImage,
          photoIndex: 0,
        },
      ]
    );
  }, [category.id, category.photos]);

  const isUnavailable =
    !!category.hasTripConflict || !!category.hasUnavailabilityBlock;

  return (
    <Box
      my={2}
      mx={isMobileView ? 0.5 : 0}
      {...(isMobileView
        ? { display: "flex", flexDirection: "column" }
        : { display: "flex", justifyContent: "start" })}
    >
      {/* Image Carousel */}
      <Box>
        <VehicleImageCarousel name={category.name} photos={formattedPhotos} />
      </Box>

      {/* Info, Pricing Select, Description, Features */}
      <Box
        display="flex"
        flexDirection="column"
        ml={isMobileView ? 0 : 5}
        width="100%"
      >
        {isUnavailable && (
          <VehicleUnavailable
            subject="Vehicle category"
            subjectLowercase="vehicle category"
          />
        )}

        <Box display="flex" justifyContent="space-between">
          {isMobileView ? (
            <Box width="100%">
              <Typography
                variant="h2"
                mb={0.5}
                {...(isUnavailable && { color: grayDark })}
              >
                {category.name}
              </Typography>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Box display="flex" flexDirection="row" my={1}>
                  <PassengerIcon color={isUnavailable ? grayDark : black} />
                  <Typography
                    variant="body2"
                    ml={1.5}
                    {...(isUnavailable && { color: grayDark })}
                  >
                    {category.capacity} Passengers
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              <Box width="100%">
                <Typography
                  variant="h2"
                  mb={0.5}
                  {...(isUnavailable && { color: grayDark })}
                >
                  {category.name}
                </Typography>
                <Box display="flex" flexDirection="column">
                  <Box display="flex" flexDirection="row" mt={1}>
                    <PassengerIcon color={isUnavailable ? grayDark : black} />
                    <Typography
                      variant="body2"
                      ml={1.5}
                      {...(isUnavailable && { color: grayDark })}
                    >
                      {category.capacity} Passengers
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Pricing Select Box */}
              <Box
                bgcolor={grayLight}
                borderRadius="4px"
                p={2}
                minWidth="240px"
              >
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  mb={1}
                  height="24px"
                >
                  {isUnavailable ? null : category.baseRateAutomation?.total !== undefined ? (
                    <VehiclePricingText
                      baseRateAutomation={category.baseRateAutomation}
                      returnBaseRateAutomation={undefined}
                    />
                  ) : (
                    <Box display="flex" alignItems="center">
                      <Typography variant="h4">Request for Pricing</Typography>
                    </Box>
                  )}
                </Box>
                {isUnavailable ? (
                  <UnavailableButton insidePanel />
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={onCategorySelect}
                  >
                    Choose Vehicle
                    <Box ml={1} />
                    <ArrowRightIcon size="small" color={white} />
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Description and Features */}
        {category.features && category.features.length > 0 && (
          <VehicleDescriptionAndFeatures
            description=""
            features={category.features}
            hasTripConflict={isUnavailable}
            activeChildSeats={[]}
          />
        )}

        {/* Mobile pricing button */}
        {isMobileView && (
          <Box>
            <Box bgcolor={grayLight} borderRadius="4px" p={2} minWidth="240px">
              <Box
                display="flex"
                justifyContent="flex-end"
                mb={1}
                height="24px"
              >
                {isUnavailable ? null : category.baseRateAutomation?.total !== undefined ? (
                  <VehiclePricingText
                    baseRateAutomation={category.baseRateAutomation}
                    returnBaseRateAutomation={undefined}
                  />
                ) : (
                  <Box display="flex" alignItems="center">
                    <Typography variant="h4">Request for Pricing</Typography>
                  </Box>
                )}
              </Box>
              {isUnavailable ? (
                <UnavailableButton insidePanel />
              ) : (
                <Button fullWidth variant="contained" onClick={onCategorySelect}>
                  Choose Vehicle
                  <Box ml={1} />
                  <ArrowRightIcon size="small" color={white} />
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default VehicleCategoryCard;
